/**
 * Open findings for one document, worst first — the server orders by severity and then
 * by the size of the money at stake, so the top of this table is the work.
 *
 * Capped by default: a short document can raise dozens of findings, and a page that
 * dumps all of them buries the few that matter.
 */
import { useState } from "react";
import { money } from "../lib/format";
import type { Exception } from "../lib/types";
import { DataTable } from "./DataTable";
import { Button, Chip, Empty, Panel, SEVERITY_TONE } from "./ui";

const SHOWN = 12;

const STRIPE: Record<string, string> = {
  high: "var(--crit)",
  medium: "var(--warn)",
  low: "var(--info)",
};

export function FlagsTable({ flags }: { flags: Exception[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? flags : flags.slice(0, SHOWN);
  const high = flags.filter((flag) => flag.severity === "high").length;

  return (
    <Panel
      title="findings"
      aside={
        flags.length ? (
          <>
            <Chip tone="crit">{flags.length} open</Chip>
            {high > 0 && <Chip tone="crit">{high} high</Chip>}
          </>
        ) : (
          <Chip tone="ok">all checks passed</Chip>
        )
      }
    >
      {flags.length === 0 ? (
        <Empty
          title="nothing to answer for"
          hint="Every verification rule passed: identities balance, dates fall in range, no duplicate keys, and totals reconcile with the document's own summary."
        />
      ) : (
        <>
          <DataTable
            table="findings"
            rows={shown}
            rowKey={(flag, index) => `${flag.rule}-${flag.claim_number ?? "doc"}-${index}`}
            columns={[
              {
                key: "severity",
                header: "",
                width: 60,
                render: (flag) => (
                  <i
                    className="block h-5 w-[3px]"
                    style={{ background: STRIPE[flag.severity] ?? "var(--text-faint)" }}
                  />
                ),
              },
              {
                key: "rule",
                header: "rule",
                width: 210,
                render: (flag) => (
                  <Chip tone={SEVERITY_TONE[flag.severity] ?? "mut"}>{flag.rule}</Chip>
                ),
              },
              {
                key: "subject",
                header: "subject",
                width: 140,
                cellClass: "font-mono text-[11.5px]",
                render: (flag) => flag.claim_number ?? <span className="text-faint">document</span>,
              },
              {
                key: "fields",
                header: "fields",
                width: 180,
                cellClass: "font-mono text-[11px] text-dim",
                render: (flag) => flag.fields.join(", ") || "—",
              },
              {
                key: "found",
                header: "what the check found",
                width: 380,
                cellClass: "text-[12.5px] leading-snug text-dim",
                render: (flag) => flag.message,
              },
              {
                key: "amount",
                header: "amount",
                width: 130,
                align: "end",
                cellClass: "tnum font-mono",
                render: (flag) => money(flag.incurred_cents) ?? <span className="text-warn">unknown</span>,
              },
              {
                // provenance counts pages from zero; a person does not
                key: "page",
                header: "page",
                width: 80,
                align: "end",
                cellClass: "tnum font-mono text-[11.5px]",
                render: (flag) => (flag.page === null ? "—" : flag.page + 1),
              },
            ]}
          />
          {flags.length > SHOWN && (
            <div className="border-t border-hair px-4 py-2.5">
              <Button variant="ghost" onClick={() => setAll((on) => !on)}>
                {all ? `show worst ${SHOWN} only` : `show all ${flags.length}`}
              </Button>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
